import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import {
    useGetAdsDataQuery,
    useGetSideQueriesQuery,
    useGetKeywordPresenceQuery,
} from 'src/context/api/competitiveIntelligence/api';
import { fDate } from 'src/utils/format-time';
import { AdItem, DomainItem } from '../types';
import { DEFAULT_API_PARAMS } from '../utils';

export const useAdsData = (selectedCountry: string | null, selectedDomain?: string) => {
    const workspace: any = useCurrentWorkspace();
    const filterStore = useSelector((state: any) => state.filters);
    
    const skipQuery = !filterStore.start || !filterStore.end || !workspace?.id;
    const skipAdsQuery = skipQuery || !selectedCountry;
    const currentCountryFilter = selectedCountry ? [selectedCountry] : [];

    // Clamp side queries date range to a max window to avoid heavy scans (performance)
    const sideMaxDays = 60; // limit to last 60 days (overrideable)
    const rawStart = filterStore.start ? new Date(filterStore.start) : null;
    const rawEnd = filterStore.end ? new Date(filterStore.end) : null;
    let sideStartISO: string | null = rawStart ? fDate(rawStart, "yyyy-MM-dd") : null;
    let sideEndISO: string | null = rawEnd ? fDate(rawEnd, "yyyy-MM-dd") : null;
    const fullRangeOverride = (typeof window !== 'undefined') && (localStorage.getItem('ci.sideQueries.fullRange') === '1');
    if (rawStart && rawEnd) {
        const msPerDay = 24 * 60 * 60 * 1000;
        const diffDays = Math.floor((rawEnd.getTime() - rawStart.getTime()) / msPerDay);
        if (!fullRangeOverride && diffDays > sideMaxDays) {
            const clampedStart = new Date(rawEnd.getTime() - sideMaxDays * msPerDay);
            sideStartISO = fDate(clampedStart, "yyyy-MM-dd");
            sideEndISO = fDate(rawEnd, "yyyy-MM-dd");
        }
    }

    const { data: keywordPresenceData, isFetching: isFetchingKeywords } = useGetKeywordPresenceQuery({
        startDate: sideStartISO,
        endDate: sideEndISO,
        device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
        countries: currentCountryFilter,
        domain: selectedDomain || '',
        workspaceTable: workspace?.bqTable,
    }, { skip: skipAdsQuery || !selectedDomain || selectedDomain.trim() === '', refetchOnFocus: false, refetchOnReconnect: false });

    // Toutes les requêtes exécutées en parallèle pour optimiser les performances
    const [
        { data: sideQueriesData, isFetching: isFetchingSide },
        { data: AdsData, isFetching: isFetchingAdsData }
    ] = [
        useGetSideQueriesQuery({
            startDate: sideStartISO,
            endDate: sideEndISO,
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore?.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            platform: filterStore?.platforms || [],
            // adType: [] // global view; if needed we can pass ['PLA'] or ['TEXT']
        }, { skip: skipAdsQuery, refetchOnFocus: false, refetchOnReconnect: false }),
        
        useGetAdsDataQuery({
            startDate: filterStore.start !== undefined ? fDate(filterStore.start, "yyyy-MM-dd") : null,
            endDate: filterStore.end !== undefined ? fDate(filterStore.end, "yyyy-MM-dd") : null,
            device: filterStore.devices?.map((device: string) => device.toLowerCase()) || [],
            countries: currentCountryFilter,
            keywords: filterStore?.keywords || [],
            workspaceTable: workspace?.bqTable,
            workspaceId: workspace?.id,
            domain: "all",
            platform: filterStore?.platforms || []
        }, { skip: skipAdsQuery }),
           ];

    // Map unified ads data
    const AdItems: AdItem[] = useMemo(() => {
        if (!AdsData?.ads) return [];
        const hasKeywordFilter = (filterStore?.keywords?.length ?? 0) === 1;
        return AdsData.ads.map((ad: any) => {
            const avgPosition = typeof ad.avg_item_position === 'number' ? ad.avg_item_position : (typeof ad.position === 'number' ? ad.position : 0);
            const freqRaw = hasKeywordFilter
                ? (ad.frequency_non_weighted != null ? ad.frequency_non_weighted : 0)
                : (ad.frequency != null ? ad.frequency : (ad.impression_share != null ? ad.impression_share * 100 : 0));
            const adTypeRaw = String(ad.ad_type || '').toUpperCase();
            const positionTypeRaw = String(ad.position_type || '').toUpperCase();
            let adType: 'PLA' | 'TEXT' | 'RightPanel' = 'PLA';
            if (positionTypeRaw.includes('JACKPOT')) {
                adType = 'RightPanel';
            } else if (positionTypeRaw.includes('TXT') || adTypeRaw === 'TEXT' || adTypeRaw === 'TXT') {
                adType = 'TEXT';
            } else {
                adType = 'PLA';
            }
            return {
                title: ad.title || '',
                price: ad.price_label || ad.price || '',
                img: ad.image || undefined,
                searchTerm: ad.search_term || '',
                position: Number((avgPosition || 0).toFixed(0)),
                frequency: Math.round(freqRaw || 0),
                domain: ad.domain_name || ad.domain || '',
                _gcs_uri_desktop: ad._gcs_uri_desktop || null,
                _gcs_uri_mobile: ad._gcs_uri_mobile || null,
                device_type: ad.device_type || null,
                brandInfringementTitle: Boolean(ad.brand_infringement_title),
                brandInfringementDescription: Boolean(ad.brand_infringement_description),
                adType,
                description: ad.description || undefined,
                seller_name: ad.seller_name || undefined,
                platform: ad.platform || undefined,
                region_name: ad.region_name || undefined,
            };
        });
    }, [AdsData, filterStore?.keywords]);

    // Map side queries top_competitors to DomainItem (value = paid_presence_rate)
    const domainItems: DomainItem[] = useMemo(() => {
        const rows = sideQueriesData?.top_competitors || [];
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows
            .map((r: any, index: number) => ({
                domain: r.domain_name,
                paidPresenceRate: Number(r.paid_presence_rate ?? 0),
                color: index === 0 ? '#4caf50' : undefined,
            }))
            .sort((a, b) => b.paidPresenceRate - a.paidPresenceRate)
            .slice(0, 20);
    }, [sideQueriesData]);


    const keywordItems = useMemo(() => {
        const rows = keywordPresenceData?.keywords || [];
        if (!Array.isArray(rows) || rows.length === 0) return [];
        return rows.map((k: any) => ({
            label: k.search_term,
            score: Number(k.paid_presence_rate ?? 0),
        }));
    }, [keywordPresenceData]);

    return {
        AdItems,
        domainItems,
        keywordItems,
        isFetchingKeywords,
        // Optimisé pour l'exécution en parallèle : le chargement est terminé quand au moins les side queries sont prêtes
        // car elles sont nécessaires pour la sélection du domaine et l'affichage de la liste de gauche
        isLoading: isFetchingSide || (isFetchingAdsData),
    };
};
